import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { Node } from '@xyflow/react';
import { useDiagramStore } from '../store/diagramStore';
import { ServiceData, NetworkZone, ServiceGroup, NodeStatus } from '../types/diagram.types';

const GROUPS: ServiceGroup[] = [
  'Gateways', 'Auth/User', 'Finance', 'Trading',
  'Market Data', 'Quick Exchange', 'Acquiring', 'Crypto', 'Compliance', 'Notifications',
];
const ZONES: NetworkZone[] = ['zone-external', 'zone-dmz', 'zone-internal', 'zone-partners'];
const STATUSES: NodeStatus[] = ['active', 'planned', 'deprecated'];

interface AddServiceModalProps {
  show: boolean;
  onHide: () => void;
}

const AddServiceModal: React.FC<AddServiceModalProps> = ({ show, onHide }) => {
  const { addService } = useDiagramStore();
  const [form, setForm] = useState<Partial<ServiceData>>({
    name: '',
    group: 'Gateways',
    zone: 'zone-internal',
    status: 'planned',
    techStack: [],
    databases: [],
    version: '',
    repository: '',
    description: '',
  });
  const [techInput, setTechInput] = useState('');
  const [dbInput, setDbInput] = useState('');

  const set = (key: keyof ServiceData, val: any) =>
    setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = () => {
    if (!form.name?.trim()) return;
    const id = `svc-${uuidv4().slice(0, 8)}`;
    const newNode: Node<ServiceData> = {
      id,
      type: 'service',
      position: { x: 400, y: 300 },
      data: {
        label: form.name!,
        name: form.name!,
        group: form.group!,
        zone: form.zone!,
        status: form.status!,
        techStack: form.techStack ?? [],
        databases: form.databases ?? [],
        version: form.version,
        repository: form.repository,
        description: form.description,
      },
    };
    addService(newNode);
    onHide();
    setForm({
      name: '', group: 'Gateways', zone: 'zone-internal',
      status: 'planned', techStack: [], databases: [],
      version: '', repository: '', description: '',
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Service</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col>
            <Form.Label>Name *</Form.Label>
            <Form.Control
              value={form.name ?? ''}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. my-service"
            />
          </Col>
          <Col>
            <Form.Label>Version</Form.Label>
            <Form.Control
              value={form.version ?? ''}
              onChange={e => set('version', e.target.value)}
              placeholder="e.g. 1.0.0"
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Label>Group</Form.Label>
            <Form.Select value={form.group} onChange={e => set('group', e.target.value as ServiceGroup)}>
              {GROUPS.map(g => <option key={g}>{g}</option>)}
            </Form.Select>
          </Col>
          <Col>
            <Form.Label>Zone</Form.Label>
            <Form.Select value={form.zone} onChange={e => set('zone', e.target.value as NetworkZone)}>
              {ZONES.map(z => <option key={z} value={z}>{z}</option>)}
            </Form.Select>
          </Col>
          <Col>
            <Form.Label>Status</Form.Label>
            <Form.Select value={form.status} onChange={e => set('status', e.target.value as NodeStatus)}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </Form.Select>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Label>Tech Stack (comma-separated)</Form.Label>
            <Form.Control
              value={techInput}
              onChange={e => {
                setTechInput(e.target.value);
                set('techStack', e.target.value.split(',').map(s => s.trim()).filter(Boolean));
              }}
              placeholder="e.g. Node.js, TypeScript, gRPC"
            />
          </Col>
          <Col>
            <Form.Label>Databases (comma-separated)</Form.Label>
            <Form.Control
              value={dbInput}
              onChange={e => {
                setDbInput(e.target.value);
                set('databases', e.target.value.split(',').map(s => s.trim()).filter(Boolean));
              }}
              placeholder="e.g. PostgreSQL, Redis"
            />
          </Col>
        </Row>
        <Form.Group className="mb-3">
          <Form.Label>Repository</Form.Label>
          <Form.Control
            value={form.repository ?? ''}
            onChange={e => set('repository', e.target.value)}
            placeholder="e.g. gitlab.com/org/my-service"
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={form.description ?? ''}
            onChange={e => set('description', e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!form.name?.trim()}>
          Add Service
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddServiceModal;
